import React from 'react';
import { Check } from 'lucide-react';

export const ApplicationStep1 = ({ onNext, onDisqualify }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">01 — The Vibe Check</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-10">Which statement resonates with you more?</h2>
    <div className="grid gap-4">
      <button
        onClick={onDisqualify}
        className="text-left p-6 border border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 transition-all group"
      >
        <div className="text-zinc-500 text-sm font-mono mb-2 group-hover:text-zinc-400">Option A</div>
        <div className="text-lg md:text-xl font-light">The Metaverse is inevitable; we should build better avatars.</div>
      </button>
      <button
        onClick={onNext}
        className="text-left p-6 border border-zinc-800 hover:bg-zinc-900 hover:border-white transition-all group"
      >
        <div className="text-zinc-500 text-sm font-mono mb-2 group-hover:text-zinc-400">Option B</div>
        <div className="text-lg md:text-xl font-light">The Metaverse is a dystopia; we should build better dinner parties.</div>
      </button>
    </div>
  </div>
);

export const ApplicationStep2 = ({ onNext, formData, handleInput }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">02 — The Biography</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-4">Drop your LinkedIn here.</h2>
    <p className="text-zinc-500 mb-6 text-sm">We want to see your professional journey.</p>
    <input
      type="text"
      autoFocus
      placeholder="https://linkedin.com/in/..."
      className="w-full bg-transparent border-b-2 border-zinc-800 focus:border-orange-500 outline-none text-2xl md:text-3xl py-4 transition-colors placeholder:text-zinc-800"
      value={formData.linkedin}
      onChange={(e) => handleInput('linkedin', e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onNext()}
    />
    <button
      onClick={onNext}
      className="mt-12 bg-white text-zinc-950 px-8 py-3 font-bold uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-all"
    >
      OK <Check size={16} className="inline ml-2" />
    </button>
  </div>
);

export const ApplicationStep3 = ({ onNext, formData, handleInput }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">03 — The Obsession</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-4">What problem are you obsessed with solving?</h2>
    <p className="text-zinc-500 mb-6 text-sm">Be specific. Tell us why this keeps you up at night.</p>
    <textarea
      autoFocus
      rows={6}
      placeholder="Type your answer here..."
      className="w-full bg-transparent border-2 border-zinc-800 focus:border-orange-500 outline-none text-xl py-4 px-4 transition-colors placeholder:text-zinc-800 resize-none"
      value={formData.obsession}
      onChange={(e) => handleInput('obsession', e.target.value)}
    />
    <button
      onClick={onNext}
      disabled={!formData.obsession?.trim()}
      className="mt-8 bg-white text-zinc-950 px-8 py-3 font-bold uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Continue <Check size={16} className="inline ml-2" />
    </button>
  </div>
);

export const ApplicationStep4 = ({ onNext, formData, handleInput }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">04 — The Thesis</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-4">Describe your solution in one sentence.</h2>
    <p className="text-zinc-500 mb-6 text-sm">Clarity &gt; Cleverness. Make it count.</p>
    <textarea
      autoFocus
      rows={3}
      placeholder="We help..."
      className="w-full bg-transparent border-2 border-zinc-800 focus:border-orange-500 outline-none text-xl py-4 px-4 transition-colors placeholder:text-zinc-800 resize-none"
      value={formData.thesis}
      onChange={(e) => handleInput('thesis', e.target.value)}
    />
    <button
      onClick={onNext}
      disabled={!formData.thesis?.trim()}
      className="mt-8 bg-white text-zinc-950 px-8 py-3 font-bold uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Continue <Check size={16} className="inline ml-2" />
    </button>
  </div>
);

export const ApplicationStep5 = ({ onNext, formData, handleInput }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">05 — The Cold Start</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-4">How do you get your first 100 users?</h2>
    <p className="text-zinc-500 mb-6 text-sm">No fluff. Just tactics. Be concrete.</p>
    <textarea
      autoFocus
      rows={6}
      placeholder="First, we'll..."
      className="w-full bg-transparent border-2 border-zinc-800 focus:border-orange-500 outline-none text-xl py-4 px-4 transition-colors placeholder:text-zinc-800 resize-none"
      value={formData.coldStart}
      onChange={(e) => handleInput('coldStart', e.target.value)}
    />
    <button
      onClick={onNext}
      disabled={!formData.coldStart?.trim()}
      className="mt-8 bg-white text-zinc-950 px-8 py-3 font-bold uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Continue <Check size={16} className="inline ml-2" />
    </button>
  </div>
);

export const ApplicationStep6 = ({ onNext, formData, handleInput, isSubmitting }) => (
  <div>
    <div className="text-orange-500 font-mono text-xs uppercase tracking-widest mb-6">06 — The Contact</div>
    <h2 className="text-3xl md:text-4xl font-bold mb-8">How do we reach you?</h2>
    <div className="space-y-6">
      <div>
        <label className="block text-zinc-500 text-sm font-mono uppercase tracking-wider mb-2">Email *</label>
        <input
          type="email"
          autoFocus
          required
          placeholder="your@email.com"
          className="w-full bg-transparent border-b-2 border-zinc-800 focus:border-orange-500 outline-none text-xl py-3 transition-colors placeholder:text-zinc-800"
          value={formData.email}
          onChange={(e) => handleInput('email', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-zinc-500 text-sm font-mono uppercase tracking-wider mb-2">Phone (Optional)</label>
        <input
          type="tel"
          placeholder="+1 (555) 123-4567"
          className="w-full bg-transparent border-b-2 border-zinc-800 focus:border-orange-500 outline-none text-xl py-3 transition-colors placeholder:text-zinc-800"
          value={formData.phone}
          onChange={(e) => handleInput('phone', e.target.value)}
        />
      </div>
    </div>
    <button
      onClick={onNext}
      disabled={!formData.email?.trim() || isSubmitting}
      className="mt-12 bg-white text-zinc-950 px-8 py-3 font-bold uppercase tracking-wider hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSubmitting ? 'Submitting...' : 'Submit Application'} <Check size={16} className="inline ml-2" />
    </button>
  </div>
);
